import React, { useState, useEffect } from 'react';
import { Calendar, Save } from 'lucide-react';
import { engineers } from '../mock';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const WeeklySchedule = () => {
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [loading, setLoading] = useState(false);

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM',
    '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM',
    '10:00 PM', '11:00 PM', '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM'
  ];

  const daysOfWeek = [
    { id: 'monday', label: 'Monday' },
    { id: 'tuesday', label: 'Tuesday' },
    { id: 'wednesday', label: 'Wednesday' },
    { id: 'thursday', label: 'Thursday' },
    { id: 'friday', label: 'Friday' }
  ];

  useEffect(() => {
    fetchWeeklySchedule();
  }, []);

  const fetchWeeklySchedule = async () => {
    try {
      const response = await axios.get(`${API}/weekly-schedule`);
      setWeeklySchedule(response.data.schedule || {});
    } catch (error) {
      console.error('Error fetching weekly schedule:', error);
    }
  };

  const toggleSlot = (day, engineerId, timeSlot) => {
    const key = `${day}-${engineerId}-${timeSlot}`;
    setWeeklySchedule(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const saveWeeklySchedule = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/weekly-schedule`, {
        schedule: weeklySchedule
      });
      toast.success('Weekly schedule saved successfully!');
    } catch (error) {
      console.error('Error saving weekly schedule:', error);
      toast.error('Failed to save weekly schedule');
    } finally {
      setLoading(false);
    }
  };

  const [selectedDay, setSelectedDay] = useState('monday');

  return (
    <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2">Weekly Recurring Schedule</h2>
          <p className="text-gray-400 text-xs sm:text-sm">
            Set default availability for Monday-Friday. This works alongside daily scheduling.
          </p>
        </div>
        <button
          onClick={saveWeeklySchedule}
          disabled={loading}
          className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 text-sm sm:text-base"
        >
          <Save size={18} className="sm:w-5 sm:h-5" />
          <span>Save Weekly Schedule</span>
        </button>
      </div>

      {/* Day Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
        {daysOfWeek.map(day => (
          <button
            key={day.id}
            onClick={() => setSelectedDay(day.id)}
            className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap text-sm ${
              selectedDay === day.id
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                : 'bg-purple-900/20 text-gray-400 hover:text-white hover:bg-purple-900/30'
            }`}
          >
            {day.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {daysOfWeek.filter(day => day.id === selectedDay).map(day => (
          <div key={day.id} className="bg-black/20 rounded-xl p-3 sm:p-4">
            {/* Scrollable table */}
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-purple-500/20">
                      <th className="text-left py-2 sm:py-3 px-2 text-gray-400 font-semibold text-xs sm:text-sm sticky left-0 bg-black/20 z-10">Time</th>
                      {engineers.map(engineer => (
                        <th key={engineer.id} className="text-center py-2 sm:py-3 px-2 text-gray-400 font-semibold">
                          <div className="text-xs sm:text-sm whitespace-nowrap">{engineer.name}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map(time => (
                      <tr key={time} className="border-b border-purple-500/10 hover:bg-purple-900/10">
                        <td className="py-2 sm:py-3 px-2 font-medium text-xs sm:text-sm sticky left-0 bg-black/20 z-10">{time}</td>
                        {engineers.map(engineer => {
                          const key = `${day.id}-${engineer.id}-${time}`;
                          const isAvailable = !weeklySchedule[key];

                          return (
                            <td key={engineer.id} className="py-2 sm:py-3 px-2 text-center">
                              <button
                                onClick={() => toggleSlot(day.id, engineer.id, time)}
                                className={`w-full min-w-[70px] sm:min-w-[90px] px-2 sm:px-3 py-1 sm:py-2 rounded-lg transition-all text-xs sm:text-sm font-medium whitespace-nowrap ${
                                  isAvailable
                                    ? 'bg-green-600/20 border border-green-500/30 text-green-300 hover:bg-green-600/30'
                                    : 'bg-red-900/30 border border-red-500/40 text-red-300 hover:bg-red-900/40'
                                }`}
                              >
                                {isAvailable ? 'Available' : 'Blocked'}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
        <p className="text-sm text-yellow-300">
          <strong>Note:</strong> This weekly schedule sets your default availability for Mon-Fri. You can still override specific dates using the Daily Schedule tab.
        </p>
      </div>
    </div>
  );
};

export default WeeklySchedule;